if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.mediaVariantMap = this.getMediaVariantMap();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', () => {
              this.setActiveMedia(mediaToSwitch.dataset.target, false);
              this.selectVariantByMedia(mediaToSwitch.dataset.target);
            });
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.selectVariantByMedia(event.detail.currentElement.dataset.mediaId);
      }

      getMediaVariantMap() {
        const script = this.querySelector('script[data-media-variant-map]');
        if (!script) return null;
        try {
          return JSON.parse(script.textContent);
        } catch (e) {
          return null;
        }
      }

      findMediaForSelectedOptions() {
        if (!this.mediaVariantMap) return null;
        const productInfo = this.closest('product-info');
        if (!productInfo) return null;
        const variantSelects = productInfo.querySelector('variant-selects');
        if (!variantSelects) return null;

        const optionGroups = variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown');

        let bestMediaId = null;
        let bestScore = -Infinity;

        for (const [mediaId, entry] of Object.entries(this.mediaVariantMap)) {
          let matches = 0;
          let mismatches = 0;

          for (const group of optionGroups) {
            const label = group.querySelector('.form__label');
            if (!label) continue;

            const matchedOption = Object.keys(entry).find(
              (name) => name !== '_available' && name !== '_' && label.textContent.includes(name)
            );
            if (!matchedOption) continue;

            const checkedInput = group.querySelector('input[type="radio"]:checked');
            const select = group.querySelector('select');
            const selectedValue = checkedInput ? checkedInput.value : select?.value;

            if (selectedValue === entry[matchedOption]) {
              matches++;
            } else {
              mismatches++;
            }
          }

          // Require at least one matching option; prefer more matches over fewer mismatches
          const score = matches - mismatches;
          if (matches > 0 && score > bestScore) {
            bestScore = score;
            bestMediaId = mediaId;
          }
        }

        return bestMediaId;
      }

      selectVariantByMedia(mediaId) {
        if (!this.mediaVariantMap) return;
        const entry = this.mediaVariantMap[mediaId];

        const productInfo = this.closest('product-info');
        if (!productInfo) return;
        const variantSelects = productInfo.querySelector('variant-selects');
        const productForm = productInfo.querySelector('product-form');

        // Extra image with no variant mapping — deselect all pills & disable add to cart
        if (!entry) {
          if (variantSelects) {
            variantSelects.querySelectorAll('input[type="radio"]').forEach((r) => (r.checked = false));
            variantSelects.querySelectorAll('[data-selected-value]').forEach((el) => (el.textContent = ''));
          }
          if (productForm) productForm.toggleSubmitButton(true, window.variantStrings.chooseVariant || 'Choose a variant');
          return;
        }

        if (!variantSelects) return;

        const isAvailable = entry._available !== false;

        let changed = false;
        const optionGroups = variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown');
        for (const group of optionGroups) {
          const label = group.querySelector('.form__label');
          if (!label) continue;

          const matchedOption = Object.keys(entry).find(
            (name) => name !== '_available' && name !== '_' && label.textContent.includes(name)
          );
          if (!matchedOption) continue;

          const targetValue = entry[matchedOption];

          // Try radio/swatch inputs
          const radio = group.querySelector(`input[value="${CSS.escape(targetValue)}"]`);
          if (radio && !radio.checked) {
            radio.checked = true;
            changed = true;

            // Update the selected value label text
            const selectedValueEl = group.querySelector('[data-selected-value]');
            if (selectedValueEl) selectedValueEl.textContent = targetValue;

            if (isAvailable) {
              // In stock: trigger normal variant change cycle
              this._galleryInitiatedChange = true;
              radio.dispatchEvent(new Event('change', { bubbles: true }));
            }
            continue;
          }

          // Try select dropdowns
          const select = group.querySelector('select');
          if (select && select.value !== targetValue) {
            select.value = targetValue;
            changed = true;

            if (isAvailable) {
              this._galleryInitiatedChange = true;
              select.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        }

        // For out-of-stock: update the button directly (no fetch cycle)
        if (!isAvailable) {
          if (productForm) productForm.toggleSubmitButton(true, window.variantStrings.soldOut);
        } else if (!changed) {
          // Returning to the already-selected available variant (e.g., swiped back)
          // Restore the button in case we previously set it to "Out of Stock"
          if (productForm) productForm.toggleSubmitButton(false);
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
